import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useMutation } from '@apollo/client';
import { TEST_CONNECTION, CREATE_CONNECTION } from 'src/utils/gql/mutations';

interface ConnectionFormProps {
  onConnectionCreated: (data: any, id: string) => void;
}

export const ConnectionForm: React.FC<ConnectionFormProps> = ({
  onConnectionCreated,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    kind: 'postgres' as 'postgres' | 'mysql',
    host: '',
    port: 5432,
    database: '',
    username: '',
    password: '',
    ssl: false,
  });

  const [testConnection, { loading: testing }] = useMutation(TEST_CONNECTION);
  const [createConnection, { loading: creating }] =
    useMutation(CREATE_CONNECTION);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      // Host cloud: suggerisci SSL e porta predefinita per Postgres/MySQL gestiti.
      if (field === 'host' && typeof value === 'string') {
        const host = value.trim().toLowerCase();
        if (
          host.includes('neon.tech') ||
          host.includes('supabase.co') ||
          host.includes('railway.app') ||
          host.includes('render.com') ||
          host.includes('planetscale.com') ||
          host.includes('psdb.cloud') ||
          host.includes('aiven.io') ||
          host.includes('digitalocean.com')
        ) {
          next.ssl = true;
        }
      }

      if (field === 'kind') {
        next.port = value === 'mysql' ? 3306 : 5432;
      }

      return next;
    });
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleTestConnection = async () => {
    try {
      console.log('Testing connection with:', formData);
      const { data } = await testConnection({
        variables: { connectionData: formData },
      });

      console.log('Test result:', data);
      const result = data?.testConnection;
      if (result?.success) {
        setTestResult({
          success: true,
          message: 'Connessione testata con successo!',
        });
      } else {
        setTestResult({
          success: false,
          message: result?.errorMessage || 'Test di connessione fallito',
        });
      }
    } catch (error: any) {
      console.error('Test connection error:', error);
      // Estrai messaggio di errore da GraphQL se disponibile
      const errorMessage =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.message ||
        error?.message ||
        'Errore sconosciuto durante il test di connessione';
      setTestResult({
        success: false,
        message: errorMessage,
      });
    }
  };

  const handleCreateConnection = async () => {
    try {
      console.log('Creating connection with:', formData);
      const { data } = await createConnection({
        variables: { connectionData: formData },
      });

      console.log('Create connection result:', data);
      onConnectionCreated(formData, data.createConnection);
    } catch (error: any) {
      console.error('Create connection error:', error);
      const errorMessage =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.result?.errors?.[0]?.message ||
        error?.message ||
        'Errore sconosciuto';
      setTestResult({
        success: false,
        message: `Errore nella creazione: ${errorMessage}`,
      });
    }
  };

  return (
    <Box sx={{ maxWidth: 800, width: '100%' }}>
      <TextField
        fullWidth
        label="Nome Connessione"
        value={formData.name}
        onChange={(e) => handleInputChange('name', e.target.value)}
        margin="normal"
        required
      />

      <FormControl fullWidth margin="normal" sx={{ minWidth: 300 }}>
        <InputLabel>Tipo Database</InputLabel>
        <Select
          value={formData.kind}
          onChange={(e) => handleInputChange('kind', e.target.value)}
          label="Tipo Database"
          sx={{
            minHeight: 56,
            '& .MuiSelect-select': {
              padding: '16px 14px 8px 14px',
              fontSize: '16px',
            },
          }}
        >
          <MenuItem value="postgres">PostgreSQL</MenuItem>
          <MenuItem value="mysql">MySQL</MenuItem>
          {/* <MenuItem value="sqlserver">SQL Server</MenuItem> */}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Host"
        value={formData.host}
        onChange={(e) => handleInputChange('host', e.target.value)}
        margin="normal"
        required
        helperText="Solo hostname Neon, es. ep-xxx.eu-central-1.aws.neon.tech (non incollare l'intera connection string)"
      />

      <TextField
        fullWidth
        label="Porta"
        type="number"
        value={formData.port || ''}
        onChange={(e) =>
          handleInputChange('port', parseInt(e.target.value) || 5432)
        }
        margin="normal"
        required
      />

      <TextField
        fullWidth
        label="Database"
        value={formData.database}
        onChange={(e) => handleInputChange('database', e.target.value)}
        margin="normal"
        required
      />

      <TextField
        fullWidth
        label="Username"
        value={formData.username}
        onChange={(e) => handleInputChange('username', e.target.value)}
        margin="normal"
        required
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={(e) => handleInputChange('password', e.target.value)}
        margin="normal"
        required
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.ssl}
            onChange={(e) => handleInputChange('ssl', e.target.checked)}
          />
        }
        label="Usa SSL"
        sx={{ mt: 2 }}
      />

      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          sx={{ mt: 2 }}
        >
          {testResult.message}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleTestConnection}
          disabled={testing || creating}
          startIcon={testing ? <CircularProgress size={20} /> : null}
        >
          {testing ? 'Testando...' : 'Testa Connessione'}
        </Button>

        <Button
          variant="contained"
          onClick={handleCreateConnection}
          disabled={creating || !testResult?.success}
          startIcon={creating ? <CircularProgress size={20} /> : null}
        >
          {creating ? 'Creando...' : 'Crea Connessione'}
        </Button>
      </Box>
    </Box>
  );
};
